import { useRef, useState, useCallback, useEffect, useMemo } from 'react';
import { View, FlatList, NativeScrollEvent, NativeSyntheticEvent, Platform, useWindowDimensions } from 'react-native';
import { useRouter, useLocalSearchParams } from 'expo-router';
import { TutorialItem } from './TutorialItem';
import { useTutorial } from '../../src/tutorial/hooks/useTutorial';
import { getTutorialLayoutStyles } from '../../src/tutorial/helpers/tutorialLayoutStyles.helper';
import { tutorialSlides } from '../../constants/tutorial';
import type { TutorialSlide } from '../../src/tutorial/types/tutorial.types';
import { useAnalytics } from '../../src/analytics/useAnalytics';
import { ANALYTICS_EVENTS } from '../../src/analytics';
import { supabase } from '../../src/lib/supabase';
import { usePendingGuestMonkeyChallengeStore } from '../../src/shared/stores/pendingGuestMonkeyChallengeStore';
import { useCaptchaToken } from '../../src/auth/turnstile/useCaptchaToken';
import { logger } from '../../src/helpers/logger.helper';
import { useDialog } from '../../src/shared/hooks/useDialog';
import { useEntryIntent } from '../../src/onboarding/hooks/useEntryIntent';
import { ENTRY_INTENT_KIND } from '../../src/onboarding/types/entryIntent.types';
import { postTutorialDestination } from '../../src/onboarding/helpers/postTutorialDestination.helper';

interface TutorialScreenProps {
  onComplete?: () => void;
  /** When provided, Done/Skip call this instead of router (e.g. when shown as modal from menu). */
  onClose?: () => void;
  /** Test ID for testing */
  testID?: string;
}

/**
 * TutorialScreen Component
 * Displays onboarding tutorial with swipe navigation
 */
export function TutorialScreen({ onComplete, onClose, testID }: TutorialScreenProps) {
  const router = useRouter();
  const params = useLocalSearchParams<{ isMenuTrigger?: string; returnTo?: string; returnUrl?: string }>();
  const { markAsSeen } = useTutorial();
  const { track } = useAnalytics();
  const { intent, setIntent } = useEntryIntent();
  const { showAlert } = useDialog();
  const { width: windowWidth, height: windowHeight } = useWindowDimensions();
  const layoutStyles = useMemo(
    () =>
      getTutorialLayoutStyles({
        width: windowWidth,
        height: windowHeight,
        isWeb: Platform.OS === 'web',
      }),
    [windowWidth, windowHeight]
  );
  const flatListRef = useRef<FlatList>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  // True while the anonymous session (captcha + signInAnonymously) is being created after "Get
  // Started" — drives a spinner on the button so the wait doesn't read as a stuck screen.
  const [isEnteringGuest, setIsEnteringGuest] = useState(false);
  const { getCaptchaToken, resetCaptcha, CaptchaWidget } = useCaptchaToken();

  const isModalMode = typeof onClose === 'function';
  const isMenuTrigger = isModalMode || params.isMenuTrigger === 'true';
  const slides: TutorialSlide[] = tutorialSlides;

  useEffect(() => {
    track(ANALYTICS_EVENTS.TUTORIAL_STARTED);
  }, [track]);

  // Default to "cold" entry intent if nothing was set upstream. Org / squad-invite
  // / guest paths set their intent before navigating into the tutorial, so this
  // only fires for organic first-time users coming from the landing page.
  useEffect(() => {
    if (isMenuTrigger) return;
    if (intent === null) {
      setIntent({ kind: ENTRY_INTENT_KIND.COLD });
    }
  }, [intent, isMenuTrigger, setIntent]);

  // Guest-first onboarding: entering guest mode = a real anonymous session + the first-pick
  // walkthrough. Shared by the "explore as a guest" link AND by finishing/skipping the tutorial
  // for a cold/organic user (see postTutorialDestination + specs/guest-first-onboarding).
  const enterGuestMode = useCallback(async () => {
    logger.debug('enterGuestMode start (tutorial)');
    setIsEnteringGuest(true);
    try {
      track(ANALYTICS_EVENTS.GUEST_MODE_STARTED);
      // Mark guest intent so post-auth landing + the "don't lose your rank" prompt know this came via the guest path.
      setIntent({ kind: ENTRY_INTENT_KIND.GUEST });
      await markAsSeen();
      const captchaToken = await getCaptchaToken();
      const { error } = await supabase.auth.signInAnonymously({ options: { captchaToken } });
      resetCaptcha();
      if (error) {
        logger.error('signInAnonymously failed (tutorial)', { message: error.message });
        setIsEnteringGuest(false);
        await showAlert({
          title: 'Couldn’t start guest mode',
          message: error.message || 'Please try again or sign up with an email.',
          type: 'default',
        });
        return;
      }
      // One-time migration: discard any legacy locally-queued Monkey challenges
      // (they were never committed to the DB; the user starts fresh as an anonymous user)
      usePendingGuestMonkeyChallengeStore.getState().setPendingQueue([]);
      // A freshly-minted guest ALWAYS goes through the onboarding questionnaire (say hi →
      // username → leagues) before their first Monkey pick — the device-global questionnaire-seen
      // flag no longer skips a new guest (see specs/onboarding-questionnaire AC-12/AC-14). This
      // path only runs when a NEW guest is minted; a returning real user or a resuming guest
      // never reach it (they resolve to /app/ via resolveFirstRunRoute).
      router.replace('/app/onboarding' as `/${string}`);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error';
      logger.error('enterGuestMode failed (tutorial)', { message });
      setIsEnteringGuest(false);
      await showAlert({
        title: 'Couldn’t start guest mode',
        message,
        type: 'default',
      });
    }
  }, [markAsSeen, router, track, getCaptchaToken, resetCaptcha, setIntent, showAlert]);

  const handleExit = useCallback(async () => {
    if (onClose) {
      onClose();
      return;
    }
    if (params.isMenuTrigger === 'true') {
      if (router.canGoBack()) {
        router.back();
      } else {
        router.replace('/app' as `/${string}`);
      }
      return;
    }

    track(ANALYTICS_EVENTS.TUTORIAL_COMPLETED);

    if (onComplete) {
      await markAsSeen();
      onComplete();
      return;
    }

    // Cold/organic user → guest mode (no sign-up wall). Org/squad-invite or an explicit
    // returnUrl → keep the sign-up path so they can join their squad / land on the deep link.
    const destination = postTutorialDestination(intent, params.returnUrl);
    if (destination.kind === 'guest-mode') {
      await enterGuestMode();
      return;
    }
    await markAsSeen();
    router.replace(destination.href as `/${string}`);
  }, [markAsSeen, onComplete, onClose, router, params.isMenuTrigger, params.returnUrl, track, intent, enterGuestMode]);

  const handleDone = handleExit;
  const handleSkip = handleExit;

  const handleNext = useCallback(() => {
    const nextIndex = currentIndex + 1;
    if (nextIndex < slides.length) {
      flatListRef.current?.scrollToIndex({
        index: nextIndex,
        animated: true,
      });
      setCurrentIndex(nextIndex);
    } else {
      handleDone();
    }
  }, [currentIndex, handleDone, slides.length]);

  const onMomentumScrollEnd = useCallback(
    (event: NativeSyntheticEvent<NativeScrollEvent>) => {
      const offsetX = event.nativeEvent.contentOffset.x;
      const index = Math.round(offsetX / windowWidth);
      const clampedIndex = Math.max(0, Math.min(index, slides.length - 1));
      setCurrentIndex(clampedIndex);
    },
    [windowWidth, slides.length]
  );

  const renderItem = useCallback(
    ({ item, index }: { item: TutorialSlide; index: number }) => {
      return (
        <View style={layoutStyles.slideWrapper}>
          <TutorialItem
            item={item}
            index={index}
            totalSlides={slides.length}
            currentIndex={currentIndex}
            onSkip={handleSkip}
            onNext={handleNext}
            onDone={handleDone}
            isMenuTrigger={isMenuTrigger}
            isBusy={isEnteringGuest}
          />
        </View>
      );
    },
    [currentIndex, handleSkip, handleNext, handleDone, isMenuTrigger, isEnteringGuest, layoutStyles.slideWrapper, slides.length]
  );

  const getItemLayout = useCallback(
    (_: unknown, index: number) => ({
      length: windowWidth,
      offset: windowWidth * index,
      index,
    }),
    [windowWidth]
  );

  return (
    <View style={layoutStyles.container} testID={testID || 'tutorial-screen'}>
      {CaptchaWidget}
      <FlatList
        ref={flatListRef}
        data={slides}
        renderItem={renderItem}
        keyExtractor={(item) => item.key}
        horizontal
        pagingEnabled
        showsHorizontalScrollIndicator={false}
        onMomentumScrollEnd={onMomentumScrollEnd}
        getItemLayout={getItemLayout}
        initialScrollIndex={0}
        snapToInterval={windowWidth}
        decelerationRate="fast"
        contentContainerStyle={layoutStyles.flatListContent}
        testID={testID ? `${testID}-flatlist` : 'tutorial-screen-flatlist'}
      />
    </View>
  );
}

